import { useCallback, useEffect, useState } from "react";
import { Card, DataTable, Badge, Button, Text, Link, EmptyState, BlockStack, InlineStack } from "@shopify/polaris";
import { getRequest, putRequest } from "../../../shared/components/functions";

const round1 = (value) => (value === null || value === undefined ? null : Math.round(Number(value) * 10) / 10);

export function SuppliersPerformance({ setToast }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [parts, setParts] = useState([]);
  const [applyingId, setApplyingId] = useState(null);

  useEffect(() => {
    async function fetchSuppliers() {
      const data = await getRequest("/api/mrp/supplier-performance?months=24");
      setSuppliers(Array.isArray(data) ? data : []);
      setLoaded(true);
    }
    fetchSuppliers();
  }, []);

  const openSupplier = useCallback(async (supplier) => {
    setSelectedSupplier(supplier);
    setParts([]);
    const data = await getRequest(`/api/mrp/supplier-performance/${supplier.supplierId}/parts?months=24`);
    setParts(Array.isArray(data) ? data : []);
  }, []);

  // The products PUT updates every field, so fetch the full product first and
  // resend it with only the lead time changed.
  const applyMeasuredLeadTime = async (part) => {
    setApplyingId(part.productId);
    const rows = await getRequest(`/api/products/${part.productId}`);
    const product = Array.isArray(rows) ? rows[0] : rows;
    if (product) {
      const measured = Math.ceil(Number(part.avgActualLeadDays));
      await putRequest(`/api/products/${part.productId}`, { ...product, LeadTimeDays: measured }, setToast);
      setParts((current) => current.map((p) => (p.productId === part.productId ? { ...p, configuredLeadDays: measured } : p)));
    }
    setApplyingId(null);
  };

  const leadTimeDelta = (part) => {
    if (part.configuredLeadDays === null || part.configuredLeadDays === undefined) return <Badge tone="attention">Not set</Badge>;
    const delta = round1(part.avgActualLeadDays - part.configuredLeadDays);
    if (delta === null) return "";
    if (delta > 7) return <Badge tone="critical">{`${delta}d slower than configured`}</Badge>;
    if (delta < -7) return <Badge tone="info">{`${Math.abs(delta)}d faster than configured`}</Badge>;
    return <Badge tone="success">On target</Badge>;
  };

  if (loaded && suppliers.length === 0) {
    return (
      <Card>
        <EmptyState heading="No receipt history" image="">
          <p>No purchase order receipts were found in the last 24 months to measure supplier lead times from.</p>
        </EmptyState>
      </Card>
    );
  }

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            Measured supplier lead times (last 24 months)
          </Text>
          <DataTable
            verticalAlign="middle"
            increasedTableDensity
            columnContentTypes={["text", "numeric", "numeric", "numeric", "numeric", "text", "text"]}
            headings={[<b>Supplier</b>, <b>Received lines</b>, <b>Avg lead (days)</b>, <b>Worst</b>, <b>On-time %</b>, <b>Promise data</b>, <b>Parts</b>]}
            rows={suppliers.map((supplier) => [
              supplier.supplierId ? (
                <Link removeUnderline url={`/companies/${supplier.supplierId}`}>
                  {supplier.supplierName || supplier.supplierId}
                </Link>
              ) : (
                supplier.supplierName || "Unknown"
              ),
              supplier.receivedLines,
              round1(supplier.avgActualLeadDays),
              supplier.worstLeadDays,
              supplier.onTimePct === null ? "" : `${round1(supplier.onTimePct)}%`,
              supplier.linesWithPromise > 0 ? <Badge tone="success">{`${supplier.linesWithPromise} lines`}</Badge> : <Badge tone="attention">No dates given</Badge>,
              <Button size="micro" onClick={() => openSupplier(supplier)}>
                Show parts
              </Button>,
            ])}
          />
        </BlockStack>
      </Card>

      {selectedSupplier && (
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingMd" as="h2">{`Parts from ${selectedSupplier.supplierName || selectedSupplier.supplierId}`}</Text>
              <Button size="slim" onClick={() => setSelectedSupplier(null)}>
                Close
              </Button>
            </InlineStack>
            <DataTable
              verticalAlign="middle"
              increasedTableDensity
              columnContentTypes={["text", "text", "numeric", "numeric", "numeric", "text", "text"]}
              headings={[<b>Part</b>, <b>Reference</b>, <b>Lines</b>, <b>Measured avg (days)</b>, <b>Configured (days)</b>, <b>Verdict</b>, <b>Actions</b>]}
              rows={parts.map((part) => [
                <Link removeUnderline url={`/products/${part.productId}`}>
                  {String(part.productName || part.productId).substring(0, 36)}
                </Link>,
                part.Productref || "",
                part.receivedLines,
                round1(part.avgActualLeadDays),
                part.configuredLeadDays ?? "",
                leadTimeDelta(part),
                <Button size="micro" loading={applyingId === part.productId} onClick={() => applyMeasuredLeadTime(part)}>
                  {`Set lead time to ${Math.ceil(Number(part.avgActualLeadDays))}d`}
                </Button>,
              ])}
            />
          </BlockStack>
        </Card>
      )}
    </BlockStack>
  );
}
